import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  StopPiece,
  StopUnit,
} from "../../../ducks/app/cargo-and-template-shared";
import Decimal from "decimal.js-light";
import { getLoadmeterValueFromUnitAndQuantity } from "dora-shared";
import CreateEditShipmentData, {
  CreateEditShipmentStop,
} from "./CreateEditShipmentData";
import { DateTime } from "luxon";
import { getAccumulatedCargoPieces } from "./get-accumulated-cargo-pieces";
import * as actions from "../../../ducks/data/economics/customer-contacts";
import { loadCustomerDeliveryLocations } from "../../../ducks/data/economics/customer-delivery-locations";
import { useAppDispatch } from "../../../redux-store";
import { watchFormChanges } from "./watch-form-changes";
import {
  roundLoadmetersToTotal,
  stopUnitLoadmeters,
} from "./stops/round-loadmeters-preserving-total";

const nullableDecimalEq = (a: Decimal | null, b: Decimal | null): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return String(a) === String(b);
};

const stopUnitChanged = (a: StopUnit, b: StopUnit): boolean =>
  !nullableDecimalEq(a.weight, b.weight) ||
  !nullableDecimalEq(a.width, b.width) ||
  !nullableDecimalEq(a.length, b.length) ||
  // !nullableDecimalEq(a.height, b.height) ||
  // !nullableDecimalEq(a.volume, b.volume) ||
  // !nullableDecimalEq(a.colli, b.colli) ||
  !nullableDecimalEq(a.quantity, b.quantity) ||
  a.stopUnitTemplateId !== b.stopUnitTemplateId ||
  a.dropoffId !== b.dropoffId ||
  a.pickupId !== b.pickupId;

const stopUnitDimensionsChanged = (a: StopUnit, b: StopUnit): boolean =>
  !nullableDecimalEq(a.quantity, b.quantity) ||
  !nullableDecimalEq(a.width, b.width) ||
  !nullableDecimalEq(a.length, b.length);

const isStopUnitComplete = (su: StopUnit): boolean =>
  !!(su.quantity && su.width && su.length);

// Stops are registered with dot paths (`dropoffList.0.date`, see StopList) and
// react-hook-form only re-renders a Controller / field array when it's written
// under that exact name. The rules build bracket paths (`dropoffList[0].date`),
// which would update the value but leave e.g. the date picker showing the old one.
const toDotIndexes = (name: string) => name.replace(/\[(\d+)\]/g, ".$1");

const useShipmentFormRules = (
  formMethods: UseFormReturn<CreateEditShipmentData>,
  setDropoffAccumulatedInputsDisabled: (disabled: boolean) => void,
  setCustomerContactId: (id: string | null) => void,
  contacts: any[] | null,
  qualitiesId: string,
  useNewStopUnits: boolean
) => {
  const dispatch = useAppDispatch();

  const { getValues, setFocus, watch } = formMethods;
  const setValue = useCallback(
    ((name: string, value: any, options?: any) =>
      formMethods.setValue(
        toDotIndexes(name) as any,
        value,
        options
      )) as UseFormReturn<CreateEditShipmentData>["setValue"],
    [formMethods.setValue]
  );

  const prevStopUnitsRef = useRef<StopUnit[]>(getValues("stopUnits") ?? []);
  const isProgrammaticInvoiceRef = useRef(false);

  const combineTags = (stops: CreateEditShipmentStop[]) =>
    stops
      .map((s) => s.tag)
      .filter((t) => t)
      .join("/");

  const reactOnSortingDateChange = useCallback(
    (changes: any) => {
      if (changes.name?.endsWith("].date") && changes.type === "change") {
        const prefix = changes.name.slice(0, changes.name.indexOf(".date"));
        setValue(`${prefix}.HAS_SORTING_DATE_BEEN_CHANGED` as any, true);
      }
    },
    [setValue]
  );

  const changeDropoffSortingDateBasedOnPickupSortingDate = useCallback(() => {
    // A pickup date being edited can be null or an invalid DateTime, and
    // DateTime.max throws on anything that isn't a DateTime.
    const pickupDates = getValues("pickupList")
      .map((p) => p.date)
      .filter((x): x is DateTime => x instanceof DateTime && x.isValid);
    const maxPickupsDate = DateTime.max(...pickupDates);
    if (!maxPickupsDate) {
      return;
    }
    const values = getValues();
    for (let i = 0; i < values.dropoffList.length; i++) {
      const dropoff = values.dropoffList[i];
      if (dropoff.date < maxPickupsDate) {
        setValue(`dropoffList[${i}].date` as any, maxPickupsDate);
      }
    }
  }, [getValues, setValue]);

  const changeSortingDateOnOpeningDateChange = useCallback(
    (changes: any) => {
      if (
        changes.name?.endsWith("].openingDate") &&
        changes.type === "change"
      ) {
        const prefix = changes.name.slice(
          0,
          changes.name.indexOf(".openingDate")
        );
        const hasSortingDateBeenChanged = getValues(
          `${prefix}.HAS_SORTING_DATE_BEEN_CHANGED` as any
        );
        if (!hasSortingDateBeenChanged) {
          const val = getValues(changes.name);
          if (val instanceof DateTime && val.isValid) {
            setValue(`${prefix}.date` as any, val);
            changeDropoffSortingDateBasedOnPickupSortingDate();
          }
        }
      }
    },
    [getValues, setValue, changeDropoffSortingDateBasedOnPickupSortingDate]
  );

  const reactOnClientChange = useCallback(
    (changes: any) => {
      if (changes.name === "clientId" && changes.type === "change") {
        const clientId = changes.values.clientId;
        if (clientId) {
          dispatch(actions.loadCustomerContacts(clientId));
          dispatch(loadCustomerDeliveryLocations(clientId));
        }
        setCustomerContactId(null);
        setValue("clientContact", "");
        setValue("clientContactPhone", "");
        setValue("clientContactEmail", "");
      }
    },
    [setValue, dispatch, setCustomerContactId]
  );

  const disableDropoffAccumulatedInputsIfOneDropoff = useCallback(() => {
    const values = getValues("dropoffList");
    if (values.length === 1) {
      setDropoffAccumulatedInputsDisabled(true);
    } else {
      setDropoffAccumulatedInputsDisabled(false);
    }
  }, [getValues, setDropoffAccumulatedInputsDisabled]);

  const reactOnClientContactChange = useCallback(
    (changes: any) => {
      if (changes.name === "clientContact" && changes.type === "change") {
        const clientName = changes.values.clientContact;
        if (contacts) {
          const contact = contacts.find((c) => c.name === clientName);
          if (contact) {
            setValue("clientContactPhone", contact.phone || "");
            setValue("clientContactEmail", contact.email || "");
          }
        }
      }
    },
    [contacts, setValue]
  );

  const resolveCustomerContactId = useCallback(
    (changes: any) => {
      if (changes.name === "clientContact" && changes.type === "change") {
        if (contacts) {
          const contact = contacts.find(
            (c) => c.name === changes.values.clientContact
          );
          if (contact) {
            setCustomerContactId(contact.id);
          } else {
            setCustomerContactId(null);
          }
        }
      }
    },
    [setCustomerContactId, contacts]
  );

  const focusNextElementIfPossible = useCallback(
    (changes: any) => {
      if (changes.type !== "change") {
        return;
      }
      if (changes.name === "type") {
        setFocus("description");
      }
      if (changes.name === "clientId") {
        if (changes.values.clientId === null) {
          return;
        }
        setFocus("invoiceRef");
      }
      if (changes.name === "assignedTeamId") {
        const el = document.getElementById(qualitiesId);
        el?.parentElement?.click();
      }
    },
    [setFocus, qualitiesId]
  );

  const reactOnPickupSortingDateChange = useCallback(
    (changes: any) => {
      if (changes.name?.endsWith("].date") && changes.type === "change") {
        changeDropoffSortingDateBasedOnPickupSortingDate();
      }
    },
    [changeDropoffSortingDateBasedOnPickupSortingDate]
  );

  const reactOnPickupOpeningDateChange = useCallback(
    (changes: any) => {
      if (
        changes.name?.endsWith("].openingDate") &&
        changes.type === "change"
      ) {
        const pickupOpeningDates = getValues("pickupList")
          .map((p) => p.openingDate)
          .filter((x): x is DateTime => x instanceof DateTime && x.isValid);
        const maxPickupsDate = DateTime.max(...pickupOpeningDates);
        if (!maxPickupsDate) {
          return;
        }
        const values = getValues();
        for (let i = 0; i < values.dropoffList.length; i++) {
          const dropoff = values.dropoffList[i];
          if (!dropoff.openingDate || dropoff.openingDate < maxPickupsDate) {
            setValue(`dropoffList[${i}].openingDate` as any, maxPickupsDate);
          }
        }
      }
    },
    [getValues, setValue]
  );

  // Recomputes the loadmeters of the given stops (and only those) from their
  // stop units. Rounding each stop on its own lets the two sides drift apart
  // (see roundLoadmetersPreservingTotal), so the stops being recomputed take
  // whatever brings their side to the rounded total of all its units, given
  // the values the side's other stops already have. When that would move a
  // stop by a hundredth or more (another stop was edited by hand), each stop
  // is rounded on its own instead.
  const recalculateLoadmetersForStops = useCallback(
    (stopIds: string[]) => {
      const stopUnits = getValues("stopUnits") as StopUnit[];
      const sides = [
        { listName: "pickupList", unitRef: "pickupId" },
        { listName: "dropoffList", unitRef: "dropoffId" },
      ] as const;
      for (const { listName, unitRef } of sides) {
        const stops = getValues(listName);
        const refsOf = (stop: CreateEditShipmentStop) =>
          [stop.id, stop.provisoryId].filter((ref): ref is string => !!ref);
        const exactLm = (stop: CreateEditShipmentStop) =>
          stopUnits
            .filter((su) => {
              const ref = su[unitRef];
              return !!ref && refsOf(stop).includes(ref);
            })
            .reduce(
              (total, su) => total.add(stopUnitLoadmeters(su)),
              new Decimal(0)
            );
        const isRecalculated = (stop: CreateEditShipmentStop) =>
          refsOf(stop).some((ref) => stopIds.includes(ref));

        const recalculated = stops
          .map((_, i) => i)
          .filter((i) => isRecalculated(stops[i]));
        if (!recalculated.length) continue;
        const exact = recalculated.map((i) => exactLm(stops[i]));

        const others = stops.filter(
          (stop) => !isRecalculated(stop) && exactLm(stop).gt(0)
        );
        const othersTotal = others.reduce<Decimal | null>((acc, stop) => {
          try {
            return acc && acc.add(String(stop.loadmeters || 0));
          } catch {
            return null; // not a number, e.g. mid-edit
          }
        }, new Decimal(0));
        const sideTotal = [...exact, ...others.map(exactLm)]
          .reduce((acc, v) => acc.add(v), new Decimal(0))
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
        const rounded =
          (othersTotal &&
            roundLoadmetersToTotal(exact, sideTotal.minus(othersTotal))) ??
          exact.map((v) => v.toDecimalPlaces(2, Decimal.ROUND_HALF_UP));

        recalculated.forEach((stopIndex, i) => {
          setValue(
            `${listName}[${stopIndex}].loadmeters` as any,
            rounded[i].toString()
          );
        });
      }
    },
    [getValues, setValue]
  );

  const calculateLoadmetersFromNewStopUnits = useCallback(
    (changes: any) => {
      if (changes.name !== "stopUnits") return;

      const current = getValues("stopUnits") as StopUnit[];
      const prev = prevStopUnitsRef.current;

      const added = current.filter(
        (c) => !prev.some((p: StopUnit) => p.id === c.id)
      );
      const removed = prev.filter(
        (p: StopUnit) => !current.some((c: StopUnit) => c.id === p.id)
      );
      const updated = current
        .filter((c) => prev.some((p: StopUnit) => p.id === c.id))
        .map((c) => ({
          before: prev.find((p: StopUnit) => p.id === c.id)!,
          after: c,
        }))
        .filter(({ before, after }) => stopUnitChanged(before, after));

      prevStopUnitsRef.current = current;

      const affectedStopIds: string[] = [];
      const affect = (...ids: (string | null)[]) => {
        for (const id of ids) if (id) affectedStopIds.push(id);
      };

      for (const unit of added) {
        if (isStopUnitComplete(unit)) {
          affect(unit.pickupId);
        }
      }

      for (const unit of removed) {
        if (isStopUnitComplete(unit)) {
          affect(unit.pickupId, unit.dropoffId);
        }
      }

      for (const { before, after } of updated) {
        if (before.dropoffId !== after.dropoffId) {
          if (isStopUnitComplete(before) || isStopUnitComplete(after)) {
            affect(before.dropoffId, after.dropoffId);
          }
        } else if (stopUnitDimensionsChanged(before, after)) {
          if (isStopUnitComplete(before) || isStopUnitComplete(after)) {
            affect(after.pickupId, after.dropoffId);
          }
        }
      }

      recalculateLoadmetersForStops(affectedStopIds);
      if (added.length && added.find((su) => su.length && su.width)) {
        const referencedStopIds = new Set(
          current.flatMap((su) => [su.pickupId, su.dropoffId]).filter(Boolean)
        );
        (["pickupList", "dropoffList"] as const).forEach((listName) => {
          const stops = getValues(listName);
          stops.forEach((stop, i) => {
            const hasStopUnits =
              (stop.id && referencedStopIds.has(stop.id)) ||
              (stop.provisoryId && referencedStopIds.has(stop.provisoryId));
            if (
              !hasStopUnits &&
              stop.loadmeters &&
              String(stop.loadmeters) !== "0"
            ) {
              setValue(`${listName}[${i}].loadmeters` as any, "0");
            }
          });
        });
      }
    },
    [getValues, setValue, recalculateLoadmetersForStops]
  );

  const calculateLoadmetersFromUnits = useCallback(
    (changes: any) => {
      if (changes.name?.includes("].pieces")) {
        const index = changes.name.substring(
          changes.name.indexOf("[") + 1,
          changes.name.indexOf("]")
        );
        const pickupOrDropoffList = changes.name.substring(
          0,
          changes.name.indexOf("[")
        );
        const pieces = getValues(
          `${pickupOrDropoffList}[${index}].pieces` as any
        );
        if (!pieces) {
          return;
        }
        const piecesWithUnits = pieces.filter(
          (p: StopPiece) => p.unit && !Number.isNaN(p.quantity)
        );
        if (piecesWithUnits.length) {
          const sum = piecesWithUnits.reduce((total: Decimal, b: StopPiece) => {
            const value = getLoadmeterValueFromUnitAndQuantity(
              b.unit as any,
              b.quantity
            );
            if (value) {
              return total.add(new Decimal(value));
            }
            return total;
          }, new Decimal(0));
          setValue(
            `${pickupOrDropoffList}[${index}].loadmeters` as any,
            sum.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString()
          );
        }
      }
    },
    [getValues, setValue]
  );

  const setCmrTagLockedForStop = useCallback(
    (changes: any) => {
      if (changes.name?.endsWith("].tag") && changes.type === "change") {
        // TODO: do that for that exact stop
        const formFragment = changes.name?.slice(
          0,
          changes.name?.indexOf(".tag") + 1
        );
        // console.log("should set locked for ", formFragment + CMR_TAG_LOCKED);
        setValue(`${formFragment}CMR_TAG_LOCKED` as any, true);
      }
    },
    [setValue]
  );

  const reactOnLoadingRefChanged = useCallback(
    (changes: any) => {
      if (changes.name === "pickupList[0].ref" && changes.type === "change") {
        setValue("LOADING_REF_LOCKED", true);
      }
    },
    [setValue]
  );

  const reactOnContainerNoChanged = useCallback(
    (changes: any) => {
      if (changes.name === "containerNo" && changes.type === "change") {
        const loadingRefLocked = getValues("LOADING_REF_LOCKED");
        if (!loadingRefLocked) {
          setValue("pickupList[0].ref" as any, changes.values.containerNo);
        }
      }
    },
    [getValues, setValue]
  );

  const reactOnPickupNumberChanged = useCallback(
    (changes: any) => {
      if (changes.name === "pickupList") {
        if (getValues("pickupList").length > 1) {
          setValue("driverCanAddDropoffs", false);
        }
      }
    },
    [getValues, setValue]
  );

  const setInvoiceRefProgrammatically = useCallback(
    (value: string) => {
      isProgrammaticInvoiceRef.current = true;
      setValue("invoiceRef", value);
      setTimeout(() => {
        isProgrammaticInvoiceRef.current = false;
      }, 0);
    },
    [setValue]
  );

  const adjustCmrTagsWhenStopsChanged = useCallback(
    (changes: any, values: CreateEditShipmentData) => {
      if (
        changes.name === "pickupList" ||
        changes.name === "dropoffList" ||
        (changes.name?.endsWith("].tag") && changes.type === "change")
      ) {
        const { pickupList, dropoffList } = values;

        if (pickupList.length === 1 && dropoffList.length === 1) {
          if (pickupList[0].tag && !dropoffList[0].CMR_TAG_LOCKED) {
            setValue(`dropoffList[0].tag` as any, pickupList[0].tag);
          }
          if (!values.INVOICE_REF_LOCKED) {
            setInvoiceRefProgrammatically(pickupList[0].tag);
          }
        }

        // if multiple pickups copy the first to the non modified ones
        // also dropoff should be automatically calculated if not manually modified
        if (pickupList.length > 1 && dropoffList.length === 1) {
          const firstPickupCmr = pickupList[0].tag;
          if (firstPickupCmr) {
            const restOfPickups = pickupList.slice(1);
            for (let i = 0; i < pickupList.length - 1; i++) {
              const pickup = restOfPickups[i];
              if (!pickup.tag) {
                setValue(`pickupList[${i + 1}].tag` as any, firstPickupCmr);
              }
            }

            const combinedTags = combineTags(pickupList);
            if (!dropoffList[0].CMR_TAG_LOCKED) {
              setValue(`dropoffList[0].tag` as any, combinedTags);
            }
            if (!values.INVOICE_REF_LOCKED) {
              setInvoiceRefProgrammatically(combinedTags);
            }
          }
        }

        // if multiple dropoffs copy the first pickup to the non modified ones
        // also pickup should be automatically calculated if not manually modified
        if (pickupList.length === 1 && dropoffList.length > 1) {
          const firstDropoffCmr = dropoffList[0].tag;
          if (firstDropoffCmr) {
            const restOfDropoffs = dropoffList.slice(1);
            for (let i = 0; i < dropoffList.length - 1; i++) {
              const dropoff = restOfDropoffs[i];
              if (!dropoff.tag) {
                setValue(`dropoffList[${i + 1}].tag` as any, firstDropoffCmr);
              }
            }

            const combinedTags = combineTags(dropoffList);
            if (!pickupList[0].CMR_TAG_LOCKED) {
              setValue(`pickupList[0].tag` as any, combinedTags);
            }
            if (!values.INVOICE_REF_LOCKED) {
              setInvoiceRefProgrammatically(combinedTags);
            }
          }
        }
      }
    },
    [setValue, setInvoiceRefProgrammatically]
  );

  const reactOnAccumulatedFieldChange = useCallback(
    (
      changes: any,
      name: keyof CreateEditShipmentStop,
      pickupPoints: CreateEditShipmentStop[]
    ) => {
      if (
        changes.name === "pickupList" ||
        (changes.name?.startsWith("pickupList[") &&
          changes.name.endsWith(`].${name as any}`) &&
          changes.type === "change")
      ) {
        const pickupsFieldsValuesSum = pickupPoints
          .map((p) => p[name])
          .reduce(
            (a, b) => a.add(new Decimal((b as string) || 0)),
            new Decimal(0)
          );
        const dropoffList = getValues("dropoffList");
        if (dropoffList.length) {
          setValue(
            `dropoffList[0].${name as any}` as any,
            pickupsFieldsValuesSum.toString()
          );
        }
      }
    },
    [setValue, getValues]
  );

  const calculateSingleDropoffUnits = useCallback(
    (pickupPoints: CreateEditShipmentStop[]) => {
      const aggregatedPieces = getAccumulatedCargoPieces(pickupPoints);

      const dropoffList = getValues("dropoffList");
      if (dropoffList.length) {
        // if set directly to the proper value it doesn't work, probably some useForms quirk
        setValue("dropoffList[0].pieces" as any, []);
        setTimeout(() => {
          setValue("dropoffList[0].pieces" as any, aggregatedPieces);
        }, 25);
      }
    },
    [setValue, getValues]
  );

  const calculatePiecesWhenPickupStopPossiblyRemoved = useCallback(
    (changes: any, pickupPoints: CreateEditShipmentStop[]) => {
      // we can't precisely identify a "removed" action, those are the only clues we have
      // at most the recalculation would trigger more often, but without unintended side effects
      if (changes.name === "pickupList" && !changes.type) {
        calculateSingleDropoffUnits(pickupPoints);
      }
    },
    [calculateSingleDropoffUnits]
  );

  const reactOnCargoPiecesChangeForOneDropoff = useCallback(
    (changes: any, pickupPoints: CreateEditShipmentStop[]) => {
      if (
        changes.name?.startsWith("pickupList") &&
        changes.name.includes("].pieces")
      ) {
        calculateSingleDropoffUnits(pickupPoints);
      }
    },
    [calculateSingleDropoffUnits]
  );

  const reactOnInvoiceRefChange = useCallback(
    (changes: any) => {
      if (
        changes.name === "invoiceRef" &&
        changes.type === "change" &&
        !isProgrammaticInvoiceRef.current
      ) {
        setValue("INVOICE_REF_LOCKED", true);
      }
    },
    [setValue]
  );

  const run = useCallback(
    (changes: any) => {
      const values = getValues();
      disableDropoffAccumulatedInputsIfOneDropoff();
      reactOnPickupSortingDateChange(changes);
      changeSortingDateOnOpeningDateChange(changes);
      reactOnSortingDateChange(changes);
      reactOnPickupOpeningDateChange(changes);
      focusNextElementIfPossible(changes);
      reactOnClientContactChange(changes);
      reactOnClientChange(changes);
      reactOnLoadingRefChanged(changes);
      reactOnContainerNoChanged(changes);
      reactOnPickupNumberChanged(changes);
      resolveCustomerContactId(changes);
      useNewStopUnits
        ? calculateLoadmetersFromNewStopUnits(changes)
        : calculateLoadmetersFromUnits(changes);
      setCmrTagLockedForStop(changes);
      reactOnInvoiceRefChange(changes);
      reactOnLoadingRefChanged(changes);
      adjustCmrTagsWhenStopsChanged(changes, values);
      if (values.dropoffList.length === 1) {
        const pickupPoints = values.pickupList;
        reactOnAccumulatedFieldChange(changes, "loadmeters", pickupPoints);
        reactOnAccumulatedFieldChange(changes, "weight", pickupPoints);
        reactOnAccumulatedFieldChange(changes, "colli", pickupPoints);
        reactOnAccumulatedFieldChange(changes, "cubicMeters", pickupPoints);
        reactOnCargoPiecesChangeForOneDropoff(changes, pickupPoints);
        calculatePiecesWhenPickupStopPossiblyRemoved(changes, pickupPoints);
      }
    },
    [
      useNewStopUnits,
      getValues,
      calculateLoadmetersFromUnits,
      calculateLoadmetersFromNewStopUnits,
      reactOnAccumulatedFieldChange,
      reactOnPickupSortingDateChange,
      disableDropoffAccumulatedInputsIfOneDropoff,
      reactOnCargoPiecesChangeForOneDropoff,
      resolveCustomerContactId,
      reactOnClientContactChange,
      reactOnContainerNoChanged,
      reactOnLoadingRefChanged,
      reactOnPickupNumberChanged,
      focusNextElementIfPossible,
      reactOnClientChange,
      // resolveDropoffCmrTagsAndInvoiceRef,
      setCmrTagLockedForStop,
      reactOnInvoiceRefChange,
      adjustCmrTagsWhenStopsChanged,
      calculatePiecesWhenPickupStopPossiblyRemoved,
      changeSortingDateOnOpeningDateChange,
      reactOnSortingDateChange,
      reactOnPickupOpeningDateChange,
    ]
  );

  const [hasNotRunTheFirstTime, setHasNotRunTheFirstTime] = useState(true);

  return useEffect(() => {
    if (hasNotRunTheFirstTime) {
      setHasNotRunTheFirstTime(false);
      return;
    }

    const subscription = watchFormChanges(watch, (changes) => {
      const dirtyFields = formMethods.formState.dirtyFields;
      // if (!(dirtyFields as any)[changes.name]) {
      //   // Field change is not marked as dirty, hence it wasn't manually edited
      //   console.log("Change is likely part of initialization or programmatic update.");
      //   return;
      // }
      if (Object.keys(dirtyFields).length === 0) {
        return;
      }

      run(changes);
    });
    return () => subscription.unsubscribe();
  }, [watch, run, hasNotRunTheFirstTime, setHasNotRunTheFirstTime]);
};

export default useShipmentFormRules;
